// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// FRONT-END-ONLY SEED DATA (alarms home), gated behind REACT_APP_FORCE_ALARM_MOCKED_DATA
// — see services/alarmsService.ts. Shaped strictly as AEM `AlarmApiRecord` rows
// (docs/upstream-specs/alarm-api.yaml): only fields the real API actually
// returns. This intentionally does NOT carry severity, causes, telemetry, or
// routine/task/optwork data — those aren't AEM concepts, and mocking them here
// would reintroduce the fabricated data this feature flag is meant to avoid.
// Records are run through the same alarmApiRecordToAlarm mapper as real API
// data, so mock mode never renders anything the real path couldn't.
//
// Every row carries an alarm_id: it is the only field that identifies one
// occurrence, and the only key POST .../alarms/{alarm_id}/apply-event accepts, so
// a row without one makes every operator action unroutable in mock mode.
// alarm_status values are open_loop states (types/alarmFsm.ts).

import type { AlarmApiRecord } from '../types/alarmApi';

const RECORDS: Omit<AlarmApiRecord, 'site_id'>[] = [
  {
    alarm_id: 'aem-evt-4471',
    alarm_code: 'ALM-4471',
    alarm_rule_id: 'ALM-4471',
    organization_id: 'qcells',
    organization_key: 'Qcells',
    site_key: 'Prime Edge Data Centers GA-10',
    equipment_id: 'ch-2',
    equipment_key: 'CH-2 Chiller',
    equipment_type: 'Cooling',
    point_id: 'pnt-ch2-cond-press',
    point_key: 'CH-2 Condenser Pressure',
    alarm_status: 'ACTIVE_UNACK',
    assigned_to: null,
    event_timestamp: '2026-08-06T13:40:00Z',
    priority: '1',
    acknowledged_by: null,
    acknowledged_at: null,
  },
  {
    alarm_id: 'aem-evt-4468',
    alarm_code: 'ALM-4468',
    alarm_rule_id: 'ALM-4468',
    organization_id: 'qcells',
    organization_key: 'Qcells',
    site_key: 'Prime Edge Data Centers GA-10',
    equipment_id: 'ups-2',
    equipment_key: 'UPS-2 Toshiba G9400',
    equipment_type: 'UPSType',
    point_id: 'pnt-ups2-cell',
    point_key: 'UPS-2 Cell Voltage',
    alarm_status: 'ACTIVE_ACK',
    assigned_to: 'Priya Nair',
    event_timestamp: '2026-07-29T13:55:00Z',
    priority: '2',
    acknowledged_by: 'Priya Nair',
    acknowledged_at: '2026-07-29T14:12:00Z',
  },
  {
    alarm_id: 'aem-evt-4390',
    alarm_code: 'ALM-4390',
    alarm_rule_id: 'ALM-4390',
    organization_id: 'qcells',
    organization_key: 'Qcells',
    site_key: 'Prime Edge Data Centers GA-10',
    equipment_id: 'ch-1',
    equipment_key: 'CH-1 Chiller',
    equipment_type: 'Cooling',
    point_id: null,
    point_key: null,
    alarm_status: 'CLEARED_ACK',
    assigned_to: 'Marcus Webb',
    event_timestamp: '2026-06-28T08:30:00Z',
    priority: '4',
    acknowledged_by: 'Marcus Webb',
    acknowledged_at: '2026-06-28T09:12:00Z',
  },
];

/**
 * Seeded AEM-shaped alarm records for `siteId`, for local/offline work when
 * REACT_APP_FORCE_ALARM_MOCKED_DATA=true. Site fields are stamped to the
 * requested site so the seed renders under whichever site is active.
 */
export function MOCK_ALARM_RECORDS(siteId: string): AlarmApiRecord[] {
  return RECORDS.map(r => ({ ...r, site_id: siteId }));
}
